import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ptw_can_create_permit,
  ptw_can_view_permit,
  headers,
  restApiUrl,
} from "./app_props";
import PermitPdf from "../components/PermitPdf";
import { MaterialReactTable } from "material-react-table";
import {
  Button,
  IconButton,
  Box,
  Drawer,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import CloseIcon from "@mui/icons-material/Close";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import * as XLSX from "xlsx";
import { formatPermitTrackingId } from "../utils/permitTracking";

const PermitList = () => {
  const user = JSON.parse(sessionStorage.getItem("userInfo"));
  const header = headers();
  const navigate = useNavigate();
  const [permits, setPermits] = useState([]);
  const [loading, setLoading] = useState(true);
  const STORAGE_KEY = "permitListState";

  // load saved state synchronously to use as initial state (avoids extra fetch)
  let _saved = null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) _saved = JSON.parse(raw);
  } catch (e) {
    _saved = null;
  }

  const [currentPage, setCurrentPage] = useState(() =>
    _saved && _saved.currentPage ? Number(_saved.currentPage) || 1 : 1,
  );
  const [pageSize, setPageSize] = useState(() =>
    _saved && _saved.pageSize ? Number(_saved.pageSize) || 10 : 10,
  );
  const [totalPages, setTotalPages] = useState(1);
  const [rowCount, setRowCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState(() =>
    _saved && _saved.searchTerm ? _saved.searchTerm : "",
  );
  const [statusFilter, setStatusFilter] = useState(() =>
    _saved && _saved.statusFilter ? _saved.statusFilter : "",
  );
  const [rowSelection, setRowSelection] = useState({});
  const [sorting, setSorting] = useState(() =>
    _saved && _saved.sorting ? _saved.sorting : [],
  );
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [filters, setFilters] = useState(() => {
    if (_saved && _saved.filters && Object.keys(_saved.filters).length > 0)
      return _saved.filters;
    if (
      _saved &&
      _saved.appliedFilters &&
      Object.keys(_saved.appliedFilters).length > 0
    )
      return _saved.appliedFilters;
    return {
      id: "",
      siteId: "",
      issuerDepartmentId: "",
      receiverDepartmentId: "",
      locationName: "",
      jobDescription: "",
      scheduleStartFrom: "",
      scheduleStartTo: "",
      priority: "",
      status: "",
    };
  });
  // `filters` are the UI inputs in the drawer. `appliedFilters` are the ones
  // currently applied to the list (only updated when Search is clicked).
  const [appliedFilters, setAppliedFilters] = useState(() => {
    if (
      _saved &&
      _saved.appliedFilters &&
      Object.keys(_saved.appliedFilters).length > 0
    )
      return _saved.appliedFilters;
    if (
      _saved &&
      _saved.filters &&
      Object.keys(_saved.filters).some(
        (k) =>
          _saved.filters[k] !== undefined &&
          _saved.filters[k] !== null &&
          _saved.filters[k] !== "",
      )
    )
      return _saved.filters;
    return {};
  });
  const [departments, setDepartments] = useState([]);

  // Persist list state to sessionStorage whenever relevant state changes
  useEffect(() => {
    try {
      const toSave = {
        currentPage,
        pageSize,
        searchTerm,
        filters,
        appliedFilters,
        sorting,
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
    } catch (err) {
      console.error("Failed to save permit list state:", err);
    }
  }, [currentPage, pageSize, searchTerm, filters, appliedFilters, sorting]);
  // Parse sites from token
  const rawSites = user.sites || [];
  let sitesList = [];
  if (typeof rawSites === "string") {
    try {
      const parsed = JSON.parse(rawSites);
      if (Array.isArray(parsed)) {
        sitesList = parsed.map((s) =>
          typeof s === "string" ? { id: null, name: s } : s,
        );
      } else if (parsed) {
        sitesList = [parsed];
      }
    } catch (e) {
      // fallback: comma-separated values
      sitesList = rawSites.split(",").map((s) => {
        const trimmed = s.trim();
        const parts = trimmed.split(":");
        if (parts.length === 2 && !isNaN(parts[0])) {
          return { id: parseInt(parts[0], 10), name: parts[1].trim() };
        }
        if (!isNaN(trimmed))
          return { id: parseInt(trimmed, 10), name: String(trimmed) };
        return { id: null, name: trimmed };
      });
    }
  } else if (Array.isArray(rawSites)) {
    sitesList = rawSites.map((s) =>
      typeof s === "string" ? { id: null, name: s } : s,
    );
  }
  const siteIds = sitesList
    .filter((site) => site.id !== null && site.id !== undefined)
    .map((site) => site.id);
  // Fetch permits when relevant state changes. Initial state is sourced from sessionStorage so
  // this will perform the correct initial fetch without a separate restore effect.
  useEffect(() => {
    const effectiveStatus =
      (appliedFilters && appliedFilters.status) || statusFilter || "";
    fetchPermits(
      currentPage,
      searchTerm,
      effectiveStatus,
      pageSize,
      sorting,
      appliedFilters,
    );
  }, [
    currentPage,
    searchTerm,
    statusFilter,
    pageSize,
    sorting,
    appliedFilters,
  ]);
  const fetchPermits = (
    page = 1,
    search = "",
    status = "",
    size = pageSize,
    sortingParams = [],
    filterParams = {},
  ) => {
    setLoading(true);
    let url = `/api/permits?page=${page - 1}&size=${size}`; // backend uses 0-based page index
    if (search) url += `&search=${encodeURIComponent(search)}`;
    if (status) url += `&status=${status}`;
    // append explicit filters as query params if provided
    if (filterParams) {
      Object.keys(filterParams).forEach((k) => {
        const v = filterParams[k];
        // Avoid sending status twice: skip filterParams.status when explicit status arg is provided
        if (k === "status" && status) return;
        if (v !== undefined && v !== null && v !== "") {
          url += `&${encodeURIComponent(k)}=${encodeURIComponent(v)}`;
        }
      });
    }
    // append sorting parameters expected by Spring Data: &sort=field,asc
    if (sortingParams && sortingParams.length > 0) {
      sortingParams.forEach((s) => {
        if (s && s.id) {
          const dir = s.desc ? "desc" : "asc";
          url += `&sort=${encodeURIComponent(s.id)},${dir}`;
        }
      });
    }
    if (siteIds.length > 0) {
      siteIds.forEach((id) => {
        url += `&accessSiteIds=${id}`;
      });
    }
    fetch(`${restApiUrl}` + url, {
      method: "GET",
      mode: "cors",
      headers: header,
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setPermits(data.data.content || []);
          setTotalPages(data.data.totalPages || 1);
          setRowCount(
            data.data.totalElements || (data.data.totalPages || 1) * size,
          );
        }
      })
      .catch((error) => console.error("Error fetching permits:", error))
      .finally(() => setLoading(false));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    // apply filters and fetch
    setDrawerOpen(false);
    const toApply = { ...filters };
    setAppliedFilters(toApply);
    // execute search using the applied filters
    fetchPermits(1, "", toApply.status || "", pageSize, sorting, toApply);
  };

  const clearFilters = () => {
    setFilters({
      id: "",
      siteId: "",
      issuerDepartmentId: "",
      receiverDepartmentId: "",
      locationName: "",
      jobDescription: "",
      scheduleStartFrom: "",
      scheduleStartTo: "",
      priority: "",
      status: "",
    });
    setSearchTerm("");
    // clear applied filters and re-fetch without filters
    setAppliedFilters({});
    // close the search drawer when clearing
    setDrawerOpen(false);
    setCurrentPage(1);
    fetchPermits(1, "", "", pageSize, sorting, {});
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "DRAFT":
        return "bg-gray-500 text-white";

      case "SUBMITTED":
        return "bg-blue-600 text-white";

      case "COMPETENT_PERSON_APPROVAL":
        return "bg-cyan-600 text-white";

      case "CLEARANCE_DEPARTMENT_REVIEW":
      case "PERMIT_TYPE_REVIEW":
        return "bg-yellow-600 text-white";

      case "EXTEND_UNDER_REVIEW":
        return "bg-amber-600 text-white";

      case "APPROVED":
        return "bg-green-600 text-white";

      case "ISSUED":
        return "bg-purple-600 text-white";

      case "ACTIVE":
        return "bg-indigo-600 text-white";

      case "SUSPENDED":
        return "bg-orange-600 text-white";

      case "EXTENDED":
        return "bg-teal-600 text-white";

      case "REJECTED":
        return "bg-red-600 text-white";

      case "CANCELLED":
        return "bg-pink-600 text-white";

      case "CLOSED":
        return "bg-zinc-700 text-white";

      default:
        return "bg-gray-500 text-white";
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "URGENT":
        return "bg-red-800 text-white";
      case "HIGH":
        return "bg-orange-500 text-white";
      case "MEDIUM":
        return "bg-yellow-500 text-white";
      case "LOW":
        return "bg-green-600 text-white";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const columns = useMemo(
    () => [
      { accessorKey: "id", header: "Id", size: 50 },
      {
        id: "trackingId",
        header: "Tracking ID",
        size: 220,
        accessorFn: (row) => formatPermitTrackingId(row),
      },
      { accessorKey: "siteName", header: "Site", size: 100 },
      { accessorKey: "issuerDepartmentName", header: "Issuer Dept", size: 100 },
      {
        accessorKey: "receiverDepartmentName",
        header: "Receiver Dept",
        size: 100,
      },
      {
        id: "permitTypes",
        header: "Permit Types",
        size: 160,
        accessorFn: (row) => (row.permitTypeBlocks || []).map((b) => b.permitTypeName).join(", "),
        Cell: ({ row }) => (
          <div className="flex flex-wrap gap-1">
            {(row.original.permitTypeBlocks || []).map((b, i) => (
              <span
                key={i}
                className="inline-flex items-center px-2 py-0.5 rounded-full text-white text-xs font-medium"
                style={{ backgroundColor: b.permitTypeColor || "#6b7280" }}
              >
                {b.permitTypeName}
              </span>
            ))}
          </div>
        ),
      },
      {
        accessorKey: "locationName",
        header: "Location",
        Cell: ({ cell }) => (
          <div style={{ maxHeight: 100, overflowY: "auto", padding: 4 }}>
            {cell.getValue()}
          </div>
        ),
      },
      {
        accessorKey: "jobDescription",
        header: "Permit Description",
        Cell: ({ cell }) => (
          <div style={{ maxHeight: 100, overflowY: "auto", padding: 4 }}>
            {cell.getValue()}
          </div>
        ),
      },
      {
        accessorKey: "scheduleStart",
        header: "Schedule Start",
        Cell: ({ cell }) =>
          cell.getValue() ? cell.getValue().replace("T", " ").slice(0, 16) : "N/A",
      },
      {
        accessorKey: "priority",
        header: "Priority",
        size: 100,
        Cell: ({ cell }) => (
          <span
            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(cell.getValue())}`}
          >
            {cell.getValue() || "MEDIUM"}
          </span>
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 100,
        Cell: ({ cell, row }) => (
          <div>
            <span
              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(cell.getValue())}`}
            >
              {cell.getValue()}
            </span>
            {cell.getValue() === "PERMIT_TYPE_REVIEW" && (
              <div className="text-xs text-yellow-700 mt-1">
                Approved:{" "}
                <strong>
                  {
                    (row.original.permitTypeBlocks || []).filter(
                      (b) => b.approved,
                    ).length
                  }
                </strong>
                /{(row.original.permitTypeBlocks || []).length}
              </div>
            )}
          </div>
        ),
      },
    ],
    [],
  );



  // Fetch departments when site changes in filters
  useEffect(() => {
    const site = filters.siteId;
    if (!site) {
      setDepartments([]);
      return;
    }
    let foundDeptList = [];
    const foundSite = sitesList.find(
      (s) => String(s.id) === String(site) || s.id === site,
    );
    if (foundSite) {
      if (foundSite.departmentList && Array.isArray(foundSite.departmentList)) {
        foundDeptList = foundSite.departmentList;
      } else if (
        foundSite.departments &&
        Array.isArray(foundSite.departments)
      ) {
        foundDeptList = foundSite.departments;
      } else {
        foundDeptList = [];
      }
      setDepartments(foundDeptList);
    }
  }, [filters.siteId]);

  // Excel export helpers (current page and all pages)
  const exportToCsv = (rows, filename) => {
    const cols = [
      { key: "id", label: "Id" },
      { key: "trackingId", label: "Tracking ID" },
      { key: "siteName", label: "Site" },
      { key: "issuerDepartmentName", label: "Issuer Dept" },
      { key: "receiverDepartmentName", label: "Receiver Dept" },
      { key: "locationName", label: "Location" },
      { key: "jobDescription", label: "Permit Description" },
      { key: "scheduleStart", label: "Schedule Start" },
      { key: "scheduleEnd", label: "Schedule End" },
      { key: "priority", label: "Priority" },
      { key: "status", label: "Status" },
    ];

    // Map rows into objects with header labels as keys
    const rowsMapped = (rows || []).map((r) => {
      const obj = {};
      cols.forEach((c) => {
        let v = c.key === "trackingId" ? formatPermitTrackingId(r) : r[c.key];
        if (c.key === "scheduleStart")
          v = v ? v.replace("T", " ").slice(0, 16) : "";
        if (c.key === "scheduleEnd") v = v ? v.replace("T", " ").slice(0, 16) : "";
        if (v === null || v === undefined) v = "";
        obj[c.label] = v;
      });
      return obj;
    });

    const ws = XLSX.utils.json_to_sheet(rowsMapped);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Permits");
    XLSX.writeFile(wb, filename);
  };

  const exportCurrentPage = () => {
    exportToCsv(permits, `permits-page-${currentPage}.xlsx`);
  };

  const exportAll = async () => {
    setLoading(true);
    try {
      const sizeForAll = rowCount && rowCount > 0 ? rowCount : 100000;
      let url = `/api/permits?page=0&size=${sizeForAll}`;
      if (searchTerm) url += `&search=${encodeURIComponent(searchTerm)}`;
      const effectiveStatus =
        (appliedFilters && appliedFilters.status) || statusFilter || "";
      if (effectiveStatus) url += `&status=${effectiveStatus}`;
      if (appliedFilters) {
        Object.keys(appliedFilters).forEach((k) => {
          const v = appliedFilters[k];
          if (k === "status" && effectiveStatus) return;
          if (v !== undefined && v !== null && v !== "") {
            url += `&${encodeURIComponent(k)}=${encodeURIComponent(v)}`;
          }
        });
      }
      if (sorting && sorting.length > 0) {
        sorting.forEach((s) => {
          if (s && s.id) {
            const dir = s.desc ? "desc" : "asc";
            url += `&sort=${encodeURIComponent(s.id)},${dir}`;
          }
        });
      }
      if (siteIds.length > 0) {
        siteIds.forEach((id) => {
          url += `&accessSiteIds=${id}`;
        });
      }

      const res = await fetch(`${restApiUrl}${url}`, {
        method: "GET",
        mode: "cors",
        headers: header,
      });
      const data = await res.json();
      if (data && data.success) {
        const rows = data.data.content || [];
        exportToCsv(rows, `permits-all.xlsx`);
      } else {
        exportToCsv(permits, `permits-page-${currentPage}.xlsx`);
      }
    } catch (e) {
      console.error("Error exporting all permits:", e);
      exportToCsv(permits, `permits-page-${currentPage}.xlsx`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="permit-list p-2 sm:p-4">
      {/* Header and filters removed; table toolbar provides actions and search */}

      {/* Permit List (MaterialReactTable with server-side pagination) */}
      {loading ? (
        <div className="text-center py-8">Loading...</div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-x-auto"  style={{WebkitOverflowScrolling:'touch'}}>
          <MaterialReactTable
            columns={columns}
            data={permits}
            enableColumnActions={false}
            enableColumnFilters={false}
            enableSorting={true}
            enableGlobalFilter={false}
            enableDensityToggle={false}
            enableFullScreenToggle={false}
            enableRowSelection
            enableSelectAll={false}
            enableMultiRowSelection={false}
            muiTableBodyRowProps={({ row }) => ({
              onClick: () => row.toggleSelected(),
              sx: { cursor: "pointer" },
            })}
            muiTableBodyCellProps={({ cell }) => ({
              sx: { textAlign: "center" },
            })}
            muiTableHeadCellProps={({ column }) => ({
              sx: {
                backgroundColor: "primary.main",
                color: "white",
                fontWeight: 600,
                textAlign: "center",
              },
            })}
            muiTableHeadProps={{
              sx: {
                position: "sticky",
                top: 0,
                zIndex: 10,
                padding: "0px !important",
              },
            }}
            muiTableContainerProps={{
              // sx: {
              //   maxHeight: tableMaxHeight,
              //   overflow: "auto",
              // },
              sx: { maxHeight: "calc(100vh - 200px)" },
            }}
            positionToolbarAlertBanner="bottom"
            onRowSelectionChange={(newSelection) =>
              setRowSelection(newSelection)
            }
            renderTopToolbarCustomActions={({ table }) => {
              const selected = table.getSelectedRowModel().rows;
              const first =
                selected && selected.length > 0 ? selected[0].original : null;
              const canView =
                user &&
                user.authorities &&
                user.authorities.includes(ptw_can_view_permit);
              const canEdit =
                user &&
                user.authorities &&
                user.authorities.includes(ptw_can_create_permit);
              const isEditable = !!(
                first &&
                canEdit &&
                first.status === "DRAFT"
              );
              const filtersApplied =
                (appliedFilters &&
                  Object.keys(appliedFilters).length > 0 &&
                  Object.keys(appliedFilters).some(
                    (k) =>
                      appliedFilters[k] !== undefined &&
                      appliedFilters[k] !== null &&
                      appliedFilters[k] !== "",
                  )) ||
                (searchTerm && searchTerm !== "") ||
                (statusFilter && statusFilter !== "");
              return (
                <div className="flex items-center gap-2">
                  {/* Search icon - opens left drawer */}
                  <Box
                    sx={{
                      width: 40,
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <IconButton
                      size="small"
                      aria-label="open-search"
                      title="Open search"
                      onClick={() => setDrawerOpen(true)}
                      sx={{ bgcolor: "primary.main", color: "white" }}
                    >
                      <SearchIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  {/* New (visible when user can create) */}
                  {user &&
                    user.authorities &&
                    user.authorities.includes(ptw_can_create_permit) && (
                      <Box
                        sx={{
                          width: 40,
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={() => navigate("/permits/new")}
                          aria-label="new-permit"
                          title="Add permit"
                          sx={{ bgcolor: "primary.main", color: "white" }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}

                  {/* View - always rendered, enabled when a row is selected and user can view */}
                  <Box
                    sx={{
                      width: 40,
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => first && navigate(`/permits/${first.id}`)}
                      aria-label="view"
                      title="View permit"
                      disabled={!first || !canView}
                      sx={{
                        bgcolor: "primary.main",
                        color: "white",
                        opacity: first && canView ? 1 : 0.45,
                      }}
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  {/* Export PDF - always rendered */}
                  <Box
                    sx={{
                      width: 40,
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <PermitPdf
                      permit={first}
                      asIcon
                      disabled={!first}
                      title="Download permit"
                      sx={{
                        bgcolor: "success.main",
                        color: "white",
                        opacity: first ? 1 : 0.45,
                      }}
                    />
                  </Box>

                  {/* Edit - always rendered, enabled when a row is selected and user can edit */}
                  <Box
                    sx={{
                      width: 40,
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={() =>
                        first && navigate(`/permits/${first.id}/edit`)
                      }
                      aria-label="edit"
                      title="Edit permit"
                      disabled={!isEditable}
                      sx={{
                        bgcolor: "success.main",
                        color: "white",
                        opacity: isEditable ? 1 : 0.45,
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  {/* CSV export buttons (current page / all results) */}
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Box
                      sx={{
                        width: 40,
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={exportCurrentPage}
                        title="Download current page"
                        sx={{ bgcolor: "primary.dark", color: "white" }}
                      >
                        <FileDownloadIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Box
                      sx={{
                        width: 40,
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={exportAll}
                        title="Download all"
                        sx={{ bgcolor: "primary.main", color: "white" }}
                      >
                        <CloudDownloadIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    {filtersApplied && (
                      <Box
                        sx={{ width: 40, display: "flex", justifyContent: "center" }}
                      >
                        <IconButton
                          size="small"
                          onClick={clearFilters}
                          title="Clear filters"
                          sx={{ bgcolor: "warning.main", color: "white" }}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                </div>
              );
            }}
            manualPagination={true}
            manualSorting={true}
            onSortingChange={(newSorting) => {
              // newSorting may be a functional updater
              let sortingObj = newSorting;
              if (typeof newSorting === "function") {
                sortingObj = newSorting(sorting || []);
              }
              // update sorting state and reset to first page; useEffect will trigger fetch
              setSorting(sortingObj || []);
              setCurrentPage(1);
            }}
            muiTablePaginationProps={{
              rowsPerPageOptions: [5, 10, 25, 50],
            }}
            onPaginationChange={(pagination) => {
              // pagination may be an updater function (functional update) or an object
              let paginationObj = pagination;
              if (typeof pagination === "function") {
                // provide current pagination as the old state to compute the new one
                paginationObj = pagination({
                  pageIndex: currentPage - 1,
                  pageSize,
                });
              }
              // paginationObj is expected to have { pageIndex, pageSize }
              const pageIndex = paginationObj?.pageIndex ?? 0;
              const newSize = paginationObj?.pageSize ?? pageSize;
              const newPage = pageIndex + 1;
              setCurrentPage(newPage);
              setPageSize(newSize);
              // Call fetchPermits immediately to ensure the API call fires
              const effectiveStatus =
                (appliedFilters && appliedFilters.status) || statusFilter || "";
              fetchPermits(
                newPage,
                searchTerm,
                effectiveStatus,
                newSize,
                sorting,
                appliedFilters,
              );
            }}
            state={{
              isLoading: loading,
              showAlertBanner: false,
              showProgressBars: false,
              rowSelection,
              pagination: { pageIndex: currentPage - 1, pageSize },
              sorting,
            }}
            rowCount={rowCount}
            renderRowActions={({ row }) => (
              <div className="flex items-center gap-2">
                {user &&
                  [ptw_can_view_permit].some((authority) =>
                    user.authorities === undefined
                      ? false
                      : user.authorities.includes(authority),
                  ) && (
                    <Box
                      sx={{
                        width: 40,
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/permits/${row.original.id}`)}
                        aria-label="view"
                        title="View permit"
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                <Box
                  sx={{ width: 40, display: "flex", justifyContent: "center" }}
                >
                  <PermitPdf
                    permit={row.original}
                    asIcon
                    title="Download permit"
                  />
                  {/* <PermitPdf permit={row.original} asIcon /> */}
                </Box>
                {row.original.status === "DRAFT" &&
                  user &&
                  [ptw_can_create_permit].some((authority) =>
                    user.authorities === undefined
                      ? false
                      : user.authorities.includes(authority),
                  ) && (
                    <Box
                      sx={{
                        width: 40,
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={() =>
                          navigate(`/permits/${row.original.id}/edit`)
                        }
                        aria-label="edit"
                        title="Edit permit"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
              </div>
            )}
          />
          {/* Left-side search drawer */}
          <Drawer
            anchor="left"
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
          >
            <div
              style={{
                width: 360,
                padding: 12,
                display: "flex",
                flexDirection: "column",
                gap: 12,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div />
                <div style={{ fontWeight: 600, fontSize: 16 }}>Search Form</div>
                <IconButton
                  size="small"
                  title="Close"
                  onClick={() => setDrawerOpen(false)}
                >
                  <CloseIcon />
                </IconButton>
              </div>

              {/* general free-text removed - use specific fields below */}

              <TextField
                label="Id"
                value={filters.id}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, id: e.target.value }))
                }
                size="small"
                fullWidth
              />

              <FormControl size="small" fullWidth>
                <InputLabel id="site-select-label">Site</InputLabel>
                <Select
                  labelId="site-select-label"
                  label="Site"
                  value={filters.siteId}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, siteId: e.target.value }))
                  }
                >
                  <MenuItem value="">All</MenuItem>
                  {sitesList.map((s, idx) => (
                    <MenuItem key={idx} value={s.id || s.name}>
                      {s.name || s}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" fullWidth>
                <InputLabel id="issuer-dept-label">Issuer Dept</InputLabel>
                <Select
                  labelId="issuer-dept-label"
                  label="Issuer Dept"
                  value={filters.issuerDepartmentId}
                  onChange={(e) =>
                    setFilters((f) => ({
                      ...f,
                      issuerDepartmentId: e.target.value,
                    }))
                  }
                >
                  <MenuItem value="">Any</MenuItem>
                  {departments.map((d, i) => (
                    <MenuItem
                      key={i}
                      value={d.id || d.departmentId || d.department}
                    >
                      {d.name || d.departmentName || d.department || d.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" fullWidth>
                <InputLabel id="receiver-dept-label">Receiver Dept</InputLabel>
                <Select
                  labelId="receiver-dept-label"
                  label="Receiver Dept"
                  value={filters.receiverDepartmentId}
                  onChange={(e) =>
                    setFilters((f) => ({
                      ...f,
                      receiverDepartmentId: e.target.value,
                    }))
                  }
                >
                  <MenuItem value="">Any</MenuItem>
                  {departments.map((d, i) => (
                    <MenuItem
                      key={i}
                      value={d.id || d.departmentId || d.department}
                    >
                      {d.name || d.departmentName || d.department || d.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Location"
                value={filters.locationName}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, locationName: e.target.value }))
                }
                size="small"
                fullWidth
              />

              <TextField
                label="Permit Description"
                value={filters.jobDescription}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, jobDescription: e.target.value }))
                }
                size="small"
                fullWidth
                multiline
              />

              <div style={{ display: "flex", gap: 8 }}>
                <TextField
                  label="Schedule From"
                  type="datetime-local"
                  size="small"
                  value={filters.scheduleStartFrom}
                  onChange={(e) =>
                    setFilters((f) => ({
                      ...f,
                      scheduleStartFrom: e.target.value,
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                  style={{ flex: 1 }}
                />
                <TextField
                  label="Schedule To"
                  type="datetime-local"
                  size="small"
                  value={filters.scheduleStartTo}
                  onChange={(e) =>
                    setFilters((f) => ({
                      ...f,
                      scheduleStartTo: e.target.value,
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                  style={{ flex: 1 }}
                />
              </div>

              <FormControl size="small" fullWidth>
                <InputLabel id="priority-label">Priority</InputLabel>
                <Select
                  labelId="priority-label"
                  label="Priority"
                  value={filters.priority}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, priority: e.target.value }))
                  }
                >
                  <MenuItem value="">Any</MenuItem>
                  <MenuItem value="URGENT">URGENT</MenuItem>
                  <MenuItem value="HIGH">HIGH</MenuItem>
                  <MenuItem value="MEDIUM">MEDIUM</MenuItem>
                  <MenuItem value="LOW">LOW</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" fullWidth>
                <InputLabel id="status-label">Status</InputLabel>
                <Select
                  labelId="status-label"
                  label="Status"
                  value={filters.status}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, status: e.target.value }))
                  }
                >
                  <MenuItem value="">Any</MenuItem>
                  <MenuItem value="DRAFT">DRAFT</MenuItem>
                  <MenuItem value="SUBMITTED">SUBMITTED</MenuItem>
                  <MenuItem value="CLEARANCE_DEPARTMENT_REVIEW">CLEARANCE_DEPARTMENT_REVIEW</MenuItem>
                  <MenuItem value="PERMIT_TYPE_REVIEW">PERMIT_TYPE_REVIEW</MenuItem>
                  <MenuItem value="APPROVED">APPROVED</MenuItem>
                  <MenuItem value="ISSUED">ISSUED</MenuItem>
                  <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                  <MenuItem value="SUSPENDED">SUSPENDED</MenuItem>
                  <MenuItem value="CLOSED">CLOSED</MenuItem>
                  <MenuItem value="CANCELLED">CANCELLED</MenuItem>
                </Select>
              </FormControl>
              {/* Footer actions: Search and Clear at bottom of form */}
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSearch}
                  style={{ flex: 1 }}
                >
                  Search
                </Button>
                <Button variant="outlined" onClick={clearFilters}>
                  Clear
                </Button>
              </div>
            </div>
          </Drawer>
        </div>
      )}

      {/* Pagination handled by MaterialReactTable */}
    </div>
  );
};

export default PermitList;
